import { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { shareContent } from '@/lib/native-share';

interface BlogShareButtonProps {
  title: string;
  /** Accroche pour le partage natif (sinon le titre). */
  text?: string;
  /** `overlay` = bouton vitré, posé sur la couverture (fond sombre translucide). */
  variant?: 'default' | 'overlay';
  /** `pill` = bouton large avec libellé ; `icon` = pastille ronde. */
  shape?: 'pill' | 'icon';
  className?: string;
}

/**
 * Partage de l'article : feuille de partage native (iOS/Android via le plugin
 * `@capacitor/share`, navigateur mobile) quand disponible, sinon copie du lien
 * dans le presse-papiers (`@/lib/native-share`). Porté depuis apps/marketing/src/components/blog-share-button.tsx.
 */
export function BlogShareButton({
  title,
  text,
  variant = 'default',
  shape = 'pill',
  className,
}: BlogShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const isOverlay = variant === 'overlay';
  const isIcon = shape === 'icon';

  async function handleShare() {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const result = await shareContent({ title, text: text ?? title, url });
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  }

  const iconColor = copied
    ? isOverlay
      ? 'text-green-300'
      : 'text-green-600'
    : isOverlay || isIcon
      ? ''
      : 'text-primary-600';

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={copied ? 'Lien copié' : "Partager l'article"}
      title="Partager l'article"
      className={cn(
        'inline-flex items-center justify-center transition active:scale-[0.95]',
        isIcon
          ? cn(
              'rounded-full',
              isOverlay
                ? 'size-9 text-white backdrop-blur-sm hover:bg-black/60'
                : 'hover:border-primary-300 hover:text-primary-600 size-10 border border-gray-200 bg-white text-gray-500',
            )
          : cn(
              'min-h-11 gap-2 self-start rounded-xl px-4 text-sm font-semibold active:scale-[0.98]',
              isOverlay
                ? 'bg-black/45 text-white ring-1 ring-white/20 backdrop-blur-sm hover:bg-black/60'
                : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
            ),
        className,
      )}
    >
      {copied ? (
        <Check className={cn(isIcon ? 'size-5' : 'size-4', iconColor)} aria-hidden />
      ) : (
        <Share2 className={cn(isIcon ? 'size-5' : 'size-4', iconColor)} aria-hidden />
      )}
      {!isIcon && (copied ? 'Lien copié !' : 'Partager')}
    </button>
  );
}
